
import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import { mapState } from 'vuex';
import Currency from '@/Models/PrimitiveModels/Currency/Currency';
import StripeCheckoutService from '@/Services/Subscription/StripeCheckoutService';
import StripeSubscriptionService from '@/Services/Subscription/StripeSubscriptionService';
import axios, { AxiosError } from 'axios';
import AppConfig from '@/AppConfig';
import * as Sentry from '@sentry/vue';
import { Plan, Subscription, SubscriptionState } from '@/classes/stripe';

@Component({
  components: {
    HelpTooltip: () => import('@/components/HelpTooltip.vue'),
  },
  computed: {
    ...mapState<SubscriptionState>('subscription', {
      activeSubscription: (state: SubscriptionState) => state.activeSubscription,
    }),
  },
})
export default class OrderSummary extends Vue {
  @Prop()
  selectedPlan!: Plan;

  @Prop()
  stripeCheckoutService!: StripeCheckoutService;

  @Prop()
  legalEntity?: string;

  @Prop()
  checkoutType?: string;

  @Prop()
  relatedPlans?: Array<Plan>;

  activeSubscription!: Subscription;

  urlPlanId?: string;

  stripeSubscriptionService: StripeSubscriptionService = new StripeSubscriptionService();

  prorate: Currency = new Currency();

  prorateLoading = true;

  isEnterprisePlan: boolean = this.selectedPlan.isEnterprisePlan;

  upgrading = false;

  pricePlan?: string = this.selectedPlan.id;

  // Emits event when payment period changes
  @Watch('pricePlan', { immediate: true })
  async paymentPeriodChanged(pricePlanId: string): Promise<void> {
    this.$emit('pricePlanChanged', this.stripeSubscriptionService.findPlan(pricePlanId));

    if (this.upgrading) {
      await this.getProratedDiscount();
    }
  }

  // Emits event when checkout type changes
  @Watch('checkoutType', { immediate: true })
  async checkoutTypeChanged(checkoutType: string): Promise<void> {
    this.upgrading = checkoutType === 'upgrade';

    if (this.upgrading) {
      await this.getProratedDiscount();
    } else {
      this.prorateLoading = false;
    }
  }

  created(): void {
    this.urlPlanId = this.$route.params.paramsId;
    this.pricePlan = this.urlPlanId || this.pricePlan;
    this.prorate.useGrouping = true;

    if (this.pricePlan) {
      this.selectedPlan = this.stripeSubscriptionService.findPlan(this.pricePlan)!;
    }
  }

  async getProratedDiscount(): Promise<void> {
    return await axios
      .get(AppConfig.getAPIBaseUrl() + '/subscription/stripe/proration/show/' + this.selectedPlan.id)
      .then((response) => {
        this.prorate.price = response.data.prorate / 100;
      })
      .catch((error: AxiosError) => {
        Sentry.captureException(error);
      })
      .finally(() => {
        this.prorateLoading = false;
      });
  }

  get returnPlanPrice(): string {
    return this.selectedPlan.price.displayPrice({
      price: this.stripeCheckoutService.calcGrossPayment(this.selectedPlan, 0, 1).price,
      overrideCurrency: this.selectedPlan.currency,
    });
  }

  get returnProrateAmount(): string {
    return this.selectedPlan.price.displayPrice({
      overrideCurrency: this.selectedPlan.currency,
      price: this.prorate.price,
      useGrouping: true,
    });
  }

  get returnSubTotal(): string {
    const calcGrossPayment = this.stripeCheckoutService.calcGrossPayment(this.selectedPlan, 0, 1, this.prorate.price);
    return this.selectedPlan.price.displayPrice({
      overrideCurrency: this.selectedPlan.currency,
      price: calcGrossPayment.price,
    });
  }

  get returnStrikethroughPrice(): string {
    return this.selectedPlan.price.displayPrice({
      overrideCurrency: this.selectedPlan.currency,
      price: this.selectedPlan.strikethrough_price || 0,
    });
  }

  get returnGrossPayment(): Currency {
    return this.stripeCheckoutService.calcGrossPayment(
      this.selectedPlan,
      this.stripeCheckoutService.country.vat_rate,
      1,
      this.prorate.price
    );
  }

  get returnVatRateTax(): Currency {
    return this.stripeCheckoutService.calcVatRateTax(
      this.selectedPlan,
      this.stripeCheckoutService.country.vat_rate,
      1,
      this.prorate.price
    );
  }

  get returnTax(): string {
    return this.selectedPlan.price.displayPrice({
      price: this.returnVatRateTax.price,
      overrideCurrency: this.selectedPlan.currency,
    });
  }

  get showTaxSales(): boolean {
    const { country_iso2 } = this.stripeCheckoutService.country;
    const { state } = this.stripeCheckoutService.customer.address;
    const states = ['MD', 'PA', 'NY', 'AZ', 'MA', 'MN', 'TN', 'TX', 'UT', 'WA', 'WI'];
    return country_iso2 === 'US' && states.includes(state);
  }

  get totalPrice(): string {
    return this.selectedPlan.price.displayPrice({
      price: this.returnGrossPayment.price,
      overrideCurrency: this.selectedPlan.currency,
    });
  }

  isPlanVisible(plan: Plan): boolean {
    return plan.currency === this.selectedPlan.currency;
  }
}
